/**
 * BreathCircle — the central animated circle showing the breath phase,
 * the countdown number, and the supportive phase text.
 *
 * Pure presentational. The parent owns the breath cycle state and passes
 * `phase`, `countdown`, `isActive` down.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Wind } from 'lucide-react';
import { BREATH_PATTERN } from './useBreathCycle';

const PHASE_COLORS = {
  inhale: 'from-cyan-500 to-blue-500',
  hold: 'from-purple-500 to-violet-500',
  exhale: 'from-amber-500 to-orange-500',
};
const PHASE_LABELS = {
  inhale: 'Breathe In',
  hold: 'Hold',
  exhale: 'Release',
};

const targetScaleFor = (phase, isActive) => {
  if (!isActive) return 1;
  if (phase === 'inhale' || phase === 'hold') return 1.4;
  return 1;
};

const transitionFor = (phase) => ({
  duration:
    phase === 'inhale' ? BREATH_PATTERN[0]
    : phase === 'exhale' ? BREATH_PATTERN[2]
    : 0.5,
  ease: 'easeInOut',
});

const BreathCircle = ({ phase, countdown, isActive }) => {
  const color = PHASE_COLORS[phase] || PHASE_COLORS.inhale;
  return (
    <div className="flex justify-center mb-8">
      <motion.div
        className={`w-48 h-48 rounded-full bg-gradient-to-br ${color} flex items-center justify-center shadow-2xl`}
        animate={{ scale: targetScaleFor(phase, isActive) }}
        transition={transitionFor(phase)}
      >
        <div className="text-center text-white">
          {isActive ? (
            <>
              <div className="text-5xl font-bold mb-1" data-testid="calm-countdown">{countdown}</div>
              <div className="text-lg opacity-90">{PHASE_LABELS[phase] || PHASE_LABELS.inhale}</div>
            </>
          ) : (
            <Wind className="w-16 h-16 opacity-80" />
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default BreathCircle;
